import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Package, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';

export default function TrackPage() {
  const [searchParams] = useSearchParams();
  const pathTrackingNumber = window.location.pathname.split('/track/')[1] || '';
  const [trackingNumber, setTrackingNumber] = useState(pathTrackingNumber || searchParams.get('number') || '');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const numberFromUrl = pathTrackingNumber || searchParams.get('number');
    if (numberFromUrl) {
      handleTrack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrack = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await api.get(`/api/PublicTracking/track/${trackingNumber}`);
      if (response.data.success) {
        setResult(response.data.data);
      } else {
        setError(response.data.message || 'لم يتم العثور على الشحنة');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'لم يتم العثور على الشحنة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12' dir='rtl'>
      <div className='container mx-auto px-4 max-w-4xl'>
        <div className='text-center mb-8'>
          <div className='inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl mb-4'>
            <Package className='w-10 h-10 text-white' />
          </div>
          <h1 className='text-4xl font-bold text-gray-900 mb-2'>تتبع شحنتك</h1>
          <p className='text-gray-600'>أدخل رقم التتبع للحصول على آخر تحديثات الشحنة</p>
        </div>

        <Card className='border-0 shadow-2xl mb-6'>
          <CardContent className='p-8'>
            <form onSubmit={handleTrack} className='flex gap-3'>
              <div className='flex-1 relative'>
                <Search className='absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
                <Input
                  value={trackingNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTrackingNumber(e.target.value)}
                  placeholder='مثال: TRG-2026-8042'
                  className='pr-12 py-6 text-lg border-2 border-gray-200 focus:border-blue-500 rounded-xl'
                  required
                />
              </div>
              <Button type='submit' disabled={loading} className='px-8 py-6 bg-gradient-to-l from-blue-600 to-purple-700 text-lg font-semibold'>
                {loading ? 'جاري البحث...' : 'تتبع'}
              </Button>
            </form>
            {error && <div className='mt-4 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700'>{error}</div>}
          </CardContent>
        </Card>

        {result && (
          <Card className='border-0 shadow-xl'>
            <CardHeader>
              <div className='flex items-center justify-between'>
                <div>
                  <p className='text-sm text-gray-500'>رقم التتبع</p>
                  <p className='text-2xl font-bold font-mono text-blue-600'>{result.trackingNumber}</p>
                </div>
                <span className='px-4 py-2 rounded-full text-sm font-semibold bg-blue-100 text-blue-700'>
                  الحالة: {result.status || 'غير معروف'}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <div className='p-4 bg-blue-50 rounded-xl'>
                  <p className='text-sm text-blue-600 font-medium mb-2 flex items-center gap-2'>
                    <MapPin className='w-4 h-4' />من
                  </p>
                  <p className='font-semibold text-gray-900'>{result.senderCity || '-'}</p>
                </div>
                <div className='p-4 bg-emerald-50 rounded-xl'>
                  <p className='text-sm text-emerald-600 font-medium mb-2 flex items-center gap-2'>
                    <MapPin className='w-4 h-4' />إلى
                  </p>
                  <p className='font-semibold text-gray-900'>{result.receiverName || '-'}</p>
                  <p className='text-sm text-gray-600 mt-1'>{result.receiverCity || '-'}</p>
                </div>
              </div>

              {result.estimatedDelivery && (
                <div className='mt-4 p-3 bg-gray-50 rounded-xl text-center'>
                  <p className='text-xs text-gray-500'>موعد التسليم المتوقع</p>
                  <p className='font-bold'>{new Date(result.estimatedDelivery).toLocaleDateString('ar-EG')}</p>
                </div>
              )}

              {result.history && result.history.length > 0 && (
                <div className='mt-8'>
                  <h3 className='font-semibold text-gray-900 mb-4 flex items-center gap-2'>
                    <Clock className='w-5 h-5' />سجل الشحنة
                  </h3>
                  <div className='space-y-4'>
                    {result.history.map((h: any) => (
                      <div key={h.id} className='flex gap-3 items-start border-r-2 border-blue-200 pr-4'>
                        <CheckCircle2 className='w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5' />
                        <div>
                          <p className='font-medium text-gray-900'>{h.status}</p>
                          {h.description && <p className='text-sm text-gray-600'>{h.description}</p>}
                          {h.location && <p className='text-sm text-gray-500'>{h.location}</p>}
                          <p className='text-xs text-gray-400'>{new Date(h.createdAt).toLocaleString('ar-EG')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {!result && !error && (
          <div className='text-center py-12'>
            <Package className='w-24 h-24 text-gray-200 mx-auto mb-4' />
            <h3 className='text-xl font-semibold text-gray-700 mb-2'>جاهز للتتبع</h3>
            <p className='text-gray-500'>أدخل رقم التتبع أعلاه</p>
          </div>
        )}

        <div className='text-center mt-8'>
          <Button variant='ghost' onClick={() => window.location.href = '/'} className='text-gray-600'>
            <ArrowRight className='w-4 h-4 ml-2' /> العودة للرئيسية
          </Button>
        </div>
      </div>
    </div>
  );
}
